import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import type { i18n } from 'i18next';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { I18nextProvider } from 'react-i18next';

import type { ApiClient } from '../lib/api/api-client';
import { ApiClientProvider } from '../lib/api/api-context';
import { FEATURES_OFF, type Features, FeaturesProvider } from '../lib/config/features-context';
import { TelegramProvider } from '../lib/telegram/telegram-context';
import type { TelegramPort } from '../lib/telegram/telegram-port';

import { createAppTheme } from './theme';

/** Props of {@link AppProviders}. */
export interface AppProvidersProps {
  telegram: TelegramPort;
  i18n: i18n;
  api: ApiClient;
  /** Product switches of the runtime config (P8 R4); every switch off when omitted. */
  features?: Features | undefined;
  children: ReactNode;
}

/**
 * App-wide providers, shared by the real boot and by `renderWithProviders` in tests.
 *
 * The theme follows Telegram live: when the trader switches light/dark while the app is open,
 * the scheme changes and the theme is rebuilt (proposal, to be validated: dark follows Telegram).
 */
export function AppProviders({
  telegram,
  i18n,
  api,
  features = FEATURES_OFF,
  children,
}: AppProvidersProps): ReactElement {
  const scheme = useTelegramColorScheme(telegram);
  const theme = useMemo(() => createAppTheme(scheme), [scheme]);
  return (
    <TelegramProvider port={telegram}>
      <ApiClientProvider client={api}>
        <FeaturesProvider features={features}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
          </ThemeProvider>
        </FeaturesProvider>
      </ApiClientProvider>
    </TelegramProvider>
  );
}

/**
 * Current Telegram colour scheme, re-rendering on each `themeChanged`. `useSyncExternalStore` is
 * React's own hook for a value held outside React: it subscribes in an effect, unsubscribes on
 * unmount or when the port changes, and re-reads the value so a change between render and
 * subscription is not missed.
 */
function useTelegramColorScheme(telegram: TelegramPort): 'light' | 'dark' {
  const subscribe = useCallback(
    (onChange: () => void) => telegram.onColorSchemeChange(onChange),
    [telegram],
  );
  const getScheme = useCallback(() => telegram.colorScheme(), [telegram]);
  return useSyncExternalStore(subscribe, getScheme);
}
